import { connected, withErrorBoundary } from 'shared'
import { absolutizePath } from 'shared/util'
import { TopLevelPage } from 'ui'
import ParkCard from 'ui/parks/ParkCard'
import { EventApi, ParkApi } from 'api'
import { addDays, format, startOfMonth, endOfMonth } from 'date-fns'
import { useEffect, useState } from 'react'
import FullCalendar from '@fullcalendar/react'
import adaptivePlugin from '@fullcalendar/adaptive'
import dayGridPlugin from '@fullcalendar/daygrid'
import interactionPlugin from "@fullcalendar/interaction"
import timeGridPlugin from '@fullcalendar/timegrid'

let calendarGlobal = [];
let intervalId = null;

const CalendarView = ({ match }) => {
  const feedItemId = match.params.id;
  const [calendar, setCalendar] = useState([{ startDate: startOfMonth(new Date()), endDate: endOfMonth(new Date()), key: 'selection' }]);
  const [events, setEvents] = useState([]);
  const [openAvailable, setOpenAvailable] = useState([]);
  const [selectedPlaces, setSelectedPlaces] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [reservableDates, setReservableDates] = useState([]);


  const parseDate = (dateTime) => format(dateTime, "yyyy-MM-dd");

  const dateParams = (calendar) => ({ start_date: parseDate(calendar.startDate), end_date: parseDate(calendar.endDate) });

  const applyFullCalendarAccessibilityFix = () => {
    document.querySelectorAll('.fc-col-header-cell[role="columnheader"], .fc-col-header-cell[role="presentation"]')
      .forEach((th) => {
        if (!th.hasAttribute("scope")) th.setAttribute("scope", "col");
      });

    document.querySelectorAll('th[role="presentation"]').forEach((th) => {
      if (!th.hasAttribute("scope")) th.setAttribute("scope", "col");
    });

    document.querySelectorAll(".fc-scrollgrid").forEach((table) => {
      const cells = table.querySelectorAll("td, th");

      if (cells.length <= 7) {
        table.removeAttribute("role");
        table.setAttribute("role", "list");

        table.querySelectorAll(".fc-daygrid-day").forEach((cell) => {
          cell.setAttribute("role", "listitem");
        });
      } else {
        table.setAttribute("role", "grid");
      }
    });

    document.querySelectorAll(".fc-daygrid-day-number").forEach((el) => {
      if (el.tagName.toLowerCase() === "button") return;

      const dateLabel = el.getAttribute("aria-label") || el.textContent?.trim();
      const button = document.createElement("button");
      button.className = el.className;
      button.textContent = el.textContent?.trim() || "";
      button.setAttribute("type", "button");
      button.setAttribute("tabindex", "0");
      button.setAttribute("aria-label", `Select date ${dateLabel || ""}`);

      if (el.id) button.id = el.id;

      el.parentNode.replaceChild(button, el);
    });
  };

  const hideCalendarLinks = () => {
    document.querySelectorAll(".fc-daygrid-day-number").forEach((el) => {
      if (el.tagName.toLowerCase() === "a") el.style.display = "none";
    });
  };

  const fetchReservableDates = () => {
    const start = new Date();
    const end = endOfMonth(new Date());
    const dates = [];

    for (let i = 0; i < (end - start) / (1000 * 60 * 60 * 24); i++) {
      const date = addDays(start, i);
      dates.push(format(date, "yyyy-MM-dd"));
    }

    Promise.all(dates.map(date => ParkApi.openAvailable({ start_date: date, end_date: date }).then(data => data.length > 0 ? { date, places: data } : null)))
      .then(results => setReservableDates(results.filter(Boolean)));
  };

  const handleReserveClick = (placeId, date) => {
    const newSelectedDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    setSelectedDate(newSelectedDate);

    const availablePlacesForDate = openAvailable.filter(place => {
      const days = Object.keys(place.open_periods || {});
      return days.some(day => {
        const today = newSelectedDate;
        const dayOffset = { sunday: 0, monday: 1, tuesday: 2, wednesday: 3, thursday: 4, friday: 5, saturday: 6 }[day.toLowerCase()];
        if (dayOffset == null) return false;
        const openDate = new Date(today);
        openDate.setDate(today.getDate() + ((dayOffset - today.getDay() + 7) % 7));
        return openDate.toISOString().split("T")[0] === newSelectedDate.toISOString().split("T")[0];
      });
    });

    setSelectedPlaces(availablePlacesForDate);
  };

  const loadData = (calendarArg = null) => {
    const activeCalendar = calendarArg || { startDate: startOfMonth(new Date()), endDate: endOfMonth(new Date()) };
    EventApi.filtered(dateParams(activeCalendar)).then(data => setEvents(data));
    ParkApi.openAvailable().then(data => setOpenAvailable(data));
    fetchReservableDates();
  };

  const printCalendar = () => {
    const nav = document.querySelector(".MainNav");
    const footer = document.querySelector("footer");

    if (nav) nav.style.setProperty("display", "none", "important");
    if (footer) footer.style.setProperty("display", "none", "important");

    const restore = () => {
      if (nav) nav.style.removeProperty("display");
      if (footer) footer.style.removeProperty("display");
      window.removeEventListener("afterprint", restore);
    };
    window.addEventListener("afterprint", restore);
    window.print();
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "p") {
        e.preventDefault();
        printCalendar();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const handleDatesSet = (info) => {
    setCalendar([{ startDate: info.start, endDate: info.end, key: 'selection' }]);
    setSelectedDate(info.start);
  };

  useEffect(() => { loadData(calendar[0]); }, []);
  useEffect(() => {
    setTimeout(applyFullCalendarAccessibilityFix, 1500);
    setTimeout(hideCalendarLinks, 50);
  }, []);

  const calendarEvents = events.flatMap(model => {
    const base = "/app#/community-calendar";
    let url = `${absolutizePath(base)}/${model.targetUid}`

    if (model.category === "multiday") {
      let startDate = new Date(`${model.date}T09:00:00`).toISOString();
      let endDate = new Date(`${model.end_date}T18:00:00`).toISOString()

      return { id: model.id, targetUid: model.targetUid, title: model.name, start: startDate, end: endDate, url: url, allDay: true };
    } else if (model.is_recurring && model.occurrences) {
      return model.occurrences.map(occurrence => ({
        id: occurrence.id,
        targetUid: model.targetUid,
        title: model.name,
        start: new Date(`${occurrence.date}T${occurrence.time}:00`).toISOString(),
        end: new Date(`${occurrence.date}T${occurrence.close_time}:00`).toISOString(),
        allDay: false,
        url: url
      }));
    } else {
      let startDate = new Date(`${model.date}T${model.open_time_local}:00`).toISOString();
      let endDate = new Date(`${model.date}T${model.close_time_local}:00`).toISOString();

      return { id: model.id, targetUid: model.targetUid, title: model.name, start: startDate, end: endDate, url: url, allDay: false };
    }
  });

  const eventClicked = (info) => {
    if (info.event?.title?.includes("Open to Reserve")) {
      const selectedDate = new Date(info.event.start);
      setSelectedDate(selectedDate);
      const formattedDate = selectedDate.toLocaleDateString('en-CA');
      ParkApi.openAvailable({ start_date: formattedDate, end_date: formattedDate }).then(data => setSelectedPlaces(data));
      handleReserveClick(info.event.extendedProps.placeId, selectedDate);
    }
  }

  calendarEvents.push(...reservableDates.map(entry => ({ id: `reserve-${entry.date}`, title: "● Open to Reserve", start: entry.date, allDay: true })));

  return (
    <TopLevelPage className="Calendar" modelId={feedItemId} base="/community-calendar">
      <h1 className="visually-hidden"> Community Calendar </h1>
      <div className="row calendar-section justify-content-center mt-3">
        <div className="col">
          <FullCalendar
            schedulerLicenseKey="0456860429-fcs-1763595698"
            plugins={[adaptivePlugin, dayGridPlugin, interactionPlugin, timeGridPlugin]}
            initialView="dayGridMonth"
            timeZone="local"
            headerToolbar={{ left: 'title', right: 'printButton today prev,next' }}
            datesSet={handleDatesSet}
            dateClick={(info) => setSelectedDate(new Date(info.dateStr))}
            eventClick={eventClicked} events={calendarEvents}
            height="auto"
            customButtons={{
              printButton: {
                text: 'Print',
                click: () => printCalendar()
              }
            }}
          />
        </div>
      </div>
      { selectedPlaces.length > 0 &&
        <section>
          <div className="row justify-content-center">
            <div className="col-md-8">
              <h1 className="section-heading-lowercase my-3">Available Amenity Reservations</h1>
            </div>
          </div>
          <div className="row">
            { selectedPlaces.map((place, index) => (
                <div key={index} className="col-sm-6 col-lg-4 col-xl-3">
                  <ParkCard park={place} base="/community-calendar" selectedDate={selectedDate instanceof Date ? format(selectedDate, "yyyy-MM-dd") : format(new Date(), "yyyy-MM-dd")} />
                </div>
              ))
            }
          </div>
        </section>
      }
    </TopLevelPage>
  );
};

export default withErrorBoundary(connected(CalendarView));
